import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Typography, Grid, Box, Button, Pagination, Tabs, Tab, Chip, Snackbar, useMediaQuery, Theme } from '@mui/material';
import { DndContext, DragOverlay, DragEndEvent, DragStartEvent, PointerSensor, TouchSensor, useSensor, useSensors, rectIntersection } from '@dnd-kit/core';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getGamesByStatus, getGamesByStatusForUser, getPublicGameStatuses, getProfileGameStatuses, getPlatforms, PlatformFilterType } from '../../services/api';
import { getFolders, addGamesToFolder, Folder } from '../../services/folders_api';
import { folderCache } from '../../services/folders_cache';
import GameCard from '../GameCard';
import DraggableGameCard from './DraggableGameCard';
import SelectionToolbar from '../folder/SelectionToolbar';
import { UserProfile } from '../../types/UserProfile';
import { Game, Platform } from '../../types/Game';
import LoadingPlaceholder from '../LoadingPlaceholder';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import GameListFilter from '../filter/GameListFilter';
import { CardSize, CardRadius, CARD_RADIUS_VALUES, isViewDefault } from './ViewSettingsDialog';
import FolderBar from '../folder/FolderBar';
import FolderListPopover from '../folder/FolderListPopover';
import FolderManageDialog from '../folder/FolderManageDialog';
import FolderEditDialog from '../folder/FolderEditDialog';
import { isFolderFeatureEnabled } from '../../utils/env';

const GAMES_PER_PAGE = 30;

const CARD_SIZE_STORAGE_KEY = 'library_card_size';
const CARD_UI_STORAGE_KEY = 'library_card_ui';
const CARD_RADIUS_STORAGE_KEY = 'library_card_radius';
const CARD_TITLE_STORAGE_KEY = 'library_card_title';

const readCardSize = (): CardSize => {
  const v = typeof window !== 'undefined' ? localStorage.getItem(CARD_SIZE_STORAGE_KEY) : null;
  return v === 'square' || v === 'landscape' ? v : 'standard';
};
const readUiVisible = (): boolean => {
  const v = typeof window !== 'undefined' ? localStorage.getItem(CARD_UI_STORAGE_KEY) : null;
  return v !== 'off';
};
const readCardRadius = (): CardRadius => {
  const v = typeof window !== 'undefined' ? localStorage.getItem(CARD_RADIUS_STORAGE_KEY) : null;
  return v === 'rounded' || v === 'sharp' ? v : 'standard';
};
const readTitleVisible = (): boolean => {
  const v = typeof window !== 'undefined' ? localStorage.getItem(CARD_TITLE_STORAGE_KEY) : null;
  return v !== 'off';
};

interface GameLibraryTabProps {
  profile: UserProfile | null;
  isPublic: boolean;
  gameCounts: number[];
  mainTabValue: string;
}

const GameLibraryTab: React.FC<GameLibraryTabProps> = ({
  profile,
  isPublic,
  gameCounts,
  mainTabValue,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams<{ username: string }>();
  const queryParams = new URLSearchParams(location.search);
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const initialStatus = parseInt(queryParams.get('status') || '1', 10);
  const initialPage = parseInt(queryParams.get('page') || '1', 10);
  const initialSearchQuery = queryParams.get('query') || '';
  const initialOrder = queryParams.get('order') || 'default';
  const initialPlatforms = queryParams.get('platforms') ? queryParams.get('platforms')!.split(',').map(Number) : [];
  const initialFilterType = (queryParams.get('filter_type') as PlatformFilterType) || 'user_platform';
  const initialFolder = queryParams.get('folder') ? parseInt(queryParams.get('folder')!, 10) : null;

  const [tabValue, setTabValue] = useState(initialStatus);
  const [page, setPage] = useState(initialPage);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [order, setOrder] = useState(initialOrder);
  const [priorityMode, setPriorityMode] = useState(false);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<number[]>(initialPlatforms);
  const [platformFilterType, setPlatformFilterType] = useState<PlatformFilterType>(initialFilterType);
  const [cardSize, setCardSize] = useState<CardSize>(readCardSize);
  const [uiVisible, setUiVisible] = useState<boolean>(readUiVisible);
  const [cardRadius, setCardRadius] = useState<CardRadius>(readCardRadius);
  const [titleVisible, setTitleVisible] = useState<boolean>(readTitleVisible);

  // フォルダ（開発中の機能フラグでゲート。env.ts の isFolderFeatureEnabled を参照）
  const folderFeature = isFolderFeatureEnabled();
  const queryClient = useQueryClient();
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(initialFolder);
  const [manageOpen, setManageOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [folderPopoverAnchor, setFolderPopoverAnchor] = useState<HTMLElement | null>(null);
  const [snackbar, setSnackbar] = useState<{ text: string; cta?: boolean } | null>(null);
  // 複数選択モード（選択はページを跨いで保持）
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [activeDrag, setActiveDrag] = useState<{ gameIds: number[]; cover: string | null } | null>(null);

  const handleCardSizeChange = (size: CardSize) => {
    setCardSize(size);
    try { localStorage.setItem(CARD_SIZE_STORAGE_KEY, size); } catch {}
  };
  const handleUiVisibleChange = (visible: boolean) => {
    setUiVisible(visible);
    try { localStorage.setItem(CARD_UI_STORAGE_KEY, visible ? 'on' : 'off'); } catch {}
  };
  const handleCardRadiusChange = (radius: CardRadius) => {
    setCardRadius(radius);
    try { localStorage.setItem(CARD_RADIUS_STORAGE_KEY, radius); } catch {}
  };
  const handleTitleVisibleChange = (visible: boolean) => {
    setTitleVisible(visible);
    try { localStorage.setItem(CARD_TITLE_STORAGE_KEY, visible ? 'on' : 'off'); } catch {}
  };
  const handleViewReset = () => {
    handleCardSizeChange('standard');
    handleCardRadiusChange('standard');
    handleUiVisibleChange(true);
    handleTitleVisibleChange(true);
  };

  const isViewNonDefault = !isViewDefault(cardSize, cardRadius, uiVisible, titleVisible);

  const scrollPositionRef = useRef<number>(0);

  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // プラットフォーム一覧を取得
  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        const data = await getPlatforms();
        setPlatforms(data);
      } catch (error) {
        console.error('プラットフォームの取得に失敗しました:', error);
      }
    };
    fetchPlatforms();
  }, []);

  // フィルターがアクティブかどうかを判定
  const hasActiveFilter = useMemo(() => {
    return selectedPlatforms.length > 0 || platformFilterType !== 'user_platform';
  }, [selectedPlatforms, platformFilterType]);

  // React Queryでゲーム一覧を取得
  const stableUsername = isPublic ? username : 'me';

  // フォルダ一覧
  const { data: foldersData } = useQuery({
    queryKey: ['folders', stableUsername],
    queryFn: () => getFolders(isPublic ? username! : undefined),
    enabled: mainTabValue === 'library' && folderFeature,
    // 変更時はキャッシュを直接更新するため、受け身の鮮度窓は長めでよい（3分）。
    staleTime: 180000,
  });
  const folders: Folder[] = foldersData || [];
  // フォルダバーの表示要否：ピン留めが1つでもある、または非ピンフォルダを選択絞り込み中。
  const showFolderBar = folders.some((f) => f.pin_order != null)
    || (selectedFolderId != null
        && folders.some((f) => f.id === selectedFolderId && f.pin_order == null));

  const {
    data: gamesData,
    isLoading,
  } = useQuery({
    queryKey: ['profileGames', stableUsername, tabValue, page, searchQuery, order, selectedPlatforms, platformFilterType, selectedFolderId],
    queryFn: async () => {
      if (isPublic) {
        return getGamesByStatusForUser(username!, tabValue, page, searchQuery, order, selectedPlatforms, platformFilterType, selectedFolderId);
      } else {
        return getGamesByStatus(tabValue, page, searchQuery, order, selectedPlatforms, platformFilterType, selectedFolderId);
      }
    },
    enabled: mainTabValue === 'library',
    staleTime: isPublic
      ? 30000
      : (hasActiveFilter ? 4000 : 2000),
  });

  const games: Game[] = gamesData?.data.results || [];
  const totalGames = gamesData?.data.count || 0;
  const gamesTotalPages = Math.ceil(totalGames / GAMES_PER_PAGE);
  const loadingGames = isLoading && !gamesData;

  // ステータス取得用のgameIds
  const gameIds = useMemo(() => games.map(game => game.game_id), [games]);

  // React Queryでゲームステータスを取得
  const { data: gameStatusesData } = useQuery({
    queryKey: ['profileGameStatuses', stableUsername, gameIds],
    queryFn: async () => {
      if (isPublic) {
        return getPublicGameStatuses(username!, gameIds);
      } else {
        return getProfileGameStatuses(gameIds);
      }
    },
    enabled: gameIds.length > 0,
    staleTime: isPublic
      ? 30000
      : (hasActiveFilter ? 4000 : 2000),
  });

  const gameStatuses: {
    [key: number]: {
      status: number | null;
      score: number | null;
      review_id?: number | null;
      review_text: string | null;
      play_start_date: string | null;
      play_end_date: string | null;
      play_time: number | null;
      sub_status: number[] | null;
      score_tags: string[] | null;
      platform: number[] | null;
      priority?: number;
      has_memo?: boolean;
    }
  } = gameStatusesData || {};

  // URLを更新する関数
  const updateUrl = useCallback((status: number, pg: number, query: string, ord: string, platformIds: number[] = [], filterType: PlatformFilterType = 'user_platform', folderId: number | null = selectedFolderId) => {
    const media = queryParams.get('media') || 'game';
    const params = new URLSearchParams({
      status: status.toString(),
      page: pg.toString(),
      query,
      order: ord,
      tab: mainTabValue,
      media,
    });
    if (platformIds.length > 0) {
      params.set('platforms', platformIds.join(','));
    }
    if (filterType !== 'user_platform') {
      params.set('filter_type', filterType);
    }
    if (folderId != null) {
      params.set('folder', folderId.toString());
    }

    const newUrl = isPublic
      ? `/profile/${username}?${params.toString()}`
      : `/profile?${params.toString()}`;
    navigate(newUrl, { replace: true });
  }, [isPublic, username, mainTabValue, navigate, queryParams, selectedFolderId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const currentScroll = scrollPositionRef.current;
    setTabValue(newValue);
    setPage(1);
    updateUrl(newValue, 1, searchQuery, order, selectedPlatforms, platformFilterType);
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScroll);
    });
    const target = event.currentTarget as HTMLElement;
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }, 0);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    updateUrl(tabValue, value, searchQuery, order, selectedPlatforms, platformFilterType);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
    updateUrl(tabValue, 1, query, order, selectedPlatforms, platformFilterType);
  };

  const handleOrderChange = (newOrder: string) => {
    setOrder(newOrder);
    setPage(1);
    updateUrl(tabValue, 1, searchQuery, newOrder, selectedPlatforms, platformFilterType);
  };

  const handlePlatformChange = (platformIds: number[]) => {
    setSelectedPlatforms(platformIds);
    setPage(1);
    updateUrl(tabValue, 1, searchQuery, order, platformIds, platformFilterType);
  };

  const handlePlatformFilterTypeChange = (filterType: PlatformFilterType) => {
    setPlatformFilterType(filterType);
    setPage(1);
    updateUrl(tabValue, 1, searchQuery, order, selectedPlatforms, filterType);
  };

  const handleFilterApply = (platformIds: number[], filterType: PlatformFilterType) => {
    setSelectedPlatforms(platformIds);
    setPlatformFilterType(filterType);
    setPage(1);
    updateUrl(tabValue, 1, searchQuery, order, platformIds, filterType);
  };

  const handleClearFilter = () => {
    setSearchQuery('');
    setOrder('default');
    setSelectedPlatforms([]);
    setPlatformFilterType('user_platform');
    setPage(1);
    updateUrl(tabValue, 1, '', 'default', [], 'user_platform');
  };

  // フォルダ選択。フォルダに入る時はステータスを「すべて」に切替（フォルダ内は複数ステータス混在のため）
  const handleFolderSelect = (folderId: number | null) => {
    setSelectedFolderId(folderId);
    setPage(1);
    let nextStatus = tabValue;
    if (folderId !== null && tabValue !== 0) {
      nextStatus = 0;
      setTabValue(0);
    }
    updateUrl(nextStatus, 1, searchQuery, order, selectedPlatforms, platformFilterType, folderId);
  };

  // DnD: ゲームカードをフォルダチップへドロップ＝追加
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  // フォルダへ追加した後のキャッシュ更新＋通知（DnD・バッチ共通）
  const afterFolderAdd = useCallback((
    folderId: number,
    res: { added: number; skipped: number; item_count: number; item_limit: number },
    folderName: string,
  ) => {
    // サーバー確定値でフォルダの件数キャッシュを直接更新（refetch しない）。
    folderCache.setCount(queryClient, folderId, res.item_count);
    queryClient.invalidateQueries({ queryKey: ['profileGameStatuses', stableUsername] });
    if (selectedFolderId === folderId) {
      queryClient.invalidateQueries({ queryKey: ['profileGames', stableUsername] });
    }
    if (res.skipped > 0) {
      // 件数上限で一部（または全部）追加できなかった → プラン誘導
      setSnackbar({
        text: res.added > 0
          ? `「${folderName}」に${res.added}本追加（${res.skipped}本は上限${res.item_limit}件超過）`
          : `「${folderName}」は上限です（${res.item_limit}件）`,
        cta: true,
      });
    } else {
      setSnackbar({
        text: res.added > 0
          ? `「${folderName}」に${res.added}本追加しました`
          : `すでに「${folderName}」に入っています`,
      });
    }
  }, [queryClient, stableUsername, selectedFolderId]);

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as any;
    if (data?.type !== 'game') return;
    const ids: number[] = data.gameIds || (data.gameId != null ? [data.gameId] : []);
    const g = games.find((x) => x.game_id === data.gameId);
    setActiveDrag({ gameIds: ids, cover: g?.cover || null });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;
    const overData = over.data.current as any;
    const activeData = active.data.current as any;
    if (overData?.type !== 'folder' || activeData?.type !== 'game') return;

    const folderId: number = overData.folderId;
    const gameIds: number[] = activeData.gameIds || (activeData.gameId != null ? [activeData.gameId] : []);
    if (gameIds.length === 0) return;

    const folderName = folders.find((f) => f.id === folderId)?.name ?? 'フォルダ';
    try {
      const res = await addGamesToFolder(folderId, gameIds);
      afterFolderAdd(folderId, res, folderName);
    } catch (e) {
      console.error('フォルダへの追加に失敗しました', e);
      setSnackbar({ text: 'フォルダへの追加に失敗しました' });
    }
  };

  // 選択モード
  const handleToggleSelect = (gameId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(gameId)) next.delete(gameId);
      else next.add(gameId);
      return next;
    });
  };

  const exitSelection = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleBatchAddToFolder = async (folderId: number) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const folderName = folders.find((f) => f.id === folderId)?.name ?? 'フォルダ';
    try {
      const res = await addGamesToFolder(folderId, ids);
      afterFolderAdd(folderId, res, folderName);
    } catch (e) {
      console.error('フォルダへの追加に失敗しました', e);
      setSnackbar({ text: 'フォルダへの追加に失敗しました' });
    }
  };

  // カードのピッカーでフォルダ所属が変わった時。
  // フォルダ件数のキャッシュ更新はピッカー側で済んでいるので、ここではゲーム一覧だけ。
  const handleFolderIdsChange = useCallback(() => {
    if (selectedFolderId != null) {
      queryClient.invalidateQueries({ queryKey: ['profileGames', stableUsername] });
    }
  }, [queryClient, stableUsername, selectedFolderId]);

  // URLパラメータ変更の監視
  useEffect(() => {
    const newStatus = parseInt(queryParams.get('status') || '1', 10);
    if (newStatus !== tabValue) {
      setTabValue(newStatus);
      setPage(1);
    }
    const newFolder = queryParams.get('folder') ? parseInt(queryParams.get('folder')!, 10) : null;
    if (newFolder !== selectedFolderId) {
      setSelectedFolderId(newFolder);
      setPage(1);
    }
  }, [location.search]);

  const TabLabel: React.FC<{ label: string; count: number; isSelected: boolean }> = ({ label, count, isSelected }) => (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
      <Typography sx={{ mr: { xs: 0, sm: 1 }, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>{label}</Typography>
      {isSelected ? (
        <Chip
          label={count}
          size="small"
          sx={{
            backgroundColor: 'primary.main',
            color: 'black',
            fontSize: { xs: '0.625rem', sm: '0.75rem' },
            height: { xs: '18px', sm: '18px' },
          }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
          /{count}
        </Typography>
      )}
    </Box>
  );

  // 「すべて」は所持ステータス(プレイ済/中/中断/積み)のみ。ウィッシュリスト(index 4)は除外。
  const totalCount = gameCounts.slice(0, 4).reduce((a, b) => a + b, 0);

  return (
    <>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ sx: { display: 'none' } }}
          sx={{
            maxWidth: '100%',
            '& .MuiTab-root': {
              minWidth: isSmallScreen ? 'auto' : 'inherit',
              padding: isSmallScreen ? '6px 8px' : '6px 16px',
              minHeight: '40px',
              borderRadius: '8px',
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              }
            }
          }}
        >
          <Tab label={<TabLabel label="すべて" count={totalCount} isSelected={tabValue === 0} />} value={0} />
          <Tab label={<TabLabel label="プレイ済" count={gameCounts[0]} isSelected={tabValue === 1} />} value={1} />
          <Tab label={<TabLabel label="プレイ中" count={gameCounts[1]} isSelected={tabValue === 2} />} value={2} />
          <Tab label={<TabLabel label="中断" count={gameCounts[2]} isSelected={tabValue === 3} />} value={3} />
          <Tab label={<TabLabel label="積みゲー" count={gameCounts[3]} isSelected={tabValue === 4} />} value={4} />
          <Tab label={<TabLabel label={isSmallScreen ? "ウィッシュ" : "ウィッシュリスト"} count={gameCounts[4]} isSelected={tabValue === 5} />} value={5} />
        </Tabs>
      </Box>

      <DndContext sensors={sensors} collisionDetection={rectIntersection} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      {folderFeature && (selectionMode || showFolderBar) && (
        <Box sx={{ mb: 1 }}>
          {selectionMode ? (
            <SelectionToolbar
              count={selectedIds.size}
              folders={folders}
              onAddToFolder={handleBatchAddToFolder}
              onClear={() => setSelectedIds(new Set())}
              onDone={exitSelection}
            />
          ) : (
            <FolderBar
              folders={folders}
              selectedFolderId={selectedFolderId}
              onSelect={handleFolderSelect}
              dragInProgress={!!activeDrag}
            />
          )}
        </Box>
      )}

      <Box sx={{ mt: 1 }}>
        <GameListFilter
          onSearch={handleSearch}
          onOrderChange={handleOrderChange}
          onClearFilter={handleClearFilter}
          totalGames={totalGames}
          initialSearchQuery={searchQuery}
          initialOrder={order}
          onPriorityModeChange={!isPublic ? setPriorityMode : undefined}
          priorityMode={priorityMode}
          currentStatus={tabValue}
          platforms={platforms}
          selectedPlatforms={selectedPlatforms}
          onPlatformChange={handlePlatformChange}
          platformFilterType={platformFilterType}
          onPlatformFilterTypeChange={handlePlatformFilterTypeChange}
          onFilterApply={handleFilterApply}
          {...(!isPublic && {
            cardSize,
            uiVisible,
            titleVisible,
            cardRadius,
            isViewNonDefault,
            onCardSizeChange: handleCardSizeChange,
            onUiVisibleChange: handleUiVisibleChange,
            onTitleVisibleChange: handleTitleVisibleChange,
            onCardRadiusChange: handleCardRadiusChange,
            onViewReset: handleViewReset,
            ...(folderFeature && {
              onFolderClick: (el: HTMLElement) => setFolderPopoverAnchor(el),
              folderActive: selectedFolderId != null,
            }),
          })}
        />
      </Box>
      {loadingGames ? (
        <LoadingPlaceholder type="card" count={GAMES_PER_PAGE} />
      ) : (
        <>
          {games.length === 0 ? (
            <Box sx={{
              textAlign: 'center',
              py: 8,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              mt: 2
            }}>
              {selectedFolderId !== null ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    このフォルダにはまだゲームがありません
                  </Typography>
                  {!isPublic && (
                    <Typography variant="body1" color="text.secondary">
                      ゲームのカードをフォルダにドラッグするか、カードのメニューから追加できます
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>
                    まだゲームが登録されていません
                  </Typography>
                  {!isPublic && (
                    <>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        ゲームを検索して、ライブラリに追加しましょう
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => navigate('/search')}
                      >
                        ゲームを探す
                      </Button>
                    </>
                  )}
                </>
              )}
            </Box>
          ) : (
            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              {games.map((game) => (
                <Grid
                  item
                  xs={!isPublic && cardSize === 'landscape' ? 6 : 4}
                  sm={!isPublic && cardSize === 'landscape' ? 4 : 3}
                  md={!isPublic && cardSize === 'landscape' ? 3 : 2}
                  key={game.game_id}
                >
                  <DraggableGameCard
                    gameId={game.game_id}
                    disabled={isPublic || priorityMode}
                    selectionMode={selectionMode}
                    selected={selectedIds.has(game.game_id)}
                    onToggleSelect={handleToggleSelect}
                    dragGameIds={selectedIds.has(game.game_id) ? Array.from(selectedIds) : [game.game_id]}
                  >
                    <GameCard
                      game={game}
                      mode={isPublic ? 'public' : 'profile'}
                      username={isPublic ? username : undefined}
                      initialStatus={gameStatuses[game.game_id]?.status}
                      initialScore={gameStatuses[game.game_id]?.score}
                      initialReviewText={gameStatuses[game.game_id]?.review_text}
                      initialPlayStartDate={gameStatuses[game.game_id]?.play_start_date}
                      initialPlayEndDate={gameStatuses[game.game_id]?.play_end_date}
                      initialPlayTime={gameStatuses[game.game_id]?.play_time}
                      initialSubStatus={gameStatuses[game.game_id]?.sub_status}
                      initialScoreTags={gameStatuses[game.game_id]?.score_tags}
                      initialPlatform={gameStatuses[game.game_id]?.platform}
                      initialPriority={gameStatuses[game.game_id]?.priority}
                      initialHasMemo={gameStatuses[game.game_id]?.has_memo}
                      initialReviewId={gameStatuses[game.game_id]?.review_id}
                      priorityMode={priorityMode}
                      userSteamId={!isPublic ? profile?.steam_id : undefined}
                      folders={folderFeature && !isPublic ? folders : undefined}
                      onFolderChange={handleFolderIdsChange}
                      {...(!isPublic && {
                        cardSize,
                        uiVisible,
                        titleVisible,
                        cardRadius: CARD_RADIUS_VALUES[cardRadius],
                      })}
                    />
                  </DraggableGameCard>
                </Grid>
              ))}
            </Grid>
          )}
          {gamesTotalPages > 1 && (
            <Pagination
              count={gamesTotalPages}
              page={page}
              onChange={handlePageChange}
              sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
            />
          )}
        </>
      )}

      {/* ドラッグ中のカードプレビュー */}
      <DragOverlay dropAnimation={null}>
        {activeDrag && (
          <Box sx={{ width: { xs: 76, sm: 92 }, transform: 'rotate(-4deg)', pointerEvents: 'none' }}>
            <Box
              sx={{
                position: 'relative',
                paddingTop: '133%',
                borderRadius: '6px',
                backgroundImage: `url(${activeDrag.cover || '/image/no_image.png'})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                boxShadow: '0 14px 34px rgba(0,0,0,0.7)',
              }}
            >
              {activeDrag.gameIds.length > 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    minWidth: 26,
                    height: 26,
                    px: 0.5,
                    borderRadius: '13px',
                    backgroundColor: 'primary.main',
                    color: '#0a0a0a',
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                  }}
                >
                  {activeDrag.gameIds.length}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DragOverlay>
      </DndContext>

      {folderFeature && !isPublic && (
        <>
          <FolderListPopover
            open={Boolean(folderPopoverAnchor)}
            anchorEl={folderPopoverAnchor}
            onClose={() => setFolderPopoverAnchor(null)}
            folders={folders}
            selectedFolderId={selectedFolderId}
            onSelect={handleFolderSelect}
            onCreate={() => setCreateOpen(true)}
            onManage={() => setManageOpen(true)}
            onEnterSelection={() => setSelectionMode(true)}
          />
          <FolderManageDialog
            open={manageOpen}
            onClose={() => setManageOpen(false)}
            folders={folders}
            onFolderDeleted={(id) => { if (selectedFolderId === id) handleFolderSelect(null); }}
          />
          <FolderEditDialog
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            folder={null}
            folderCount={folders.length}
          />
        </>
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={snackbar?.cta ? 6000 : 2500}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={snackbar?.cta ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <InfoOutlinedIcon sx={{ fontSize: '1.05rem', color: 'primary.main', flexShrink: 0 }} />
            <span>{snackbar.text}</span>
          </Box>
        ) : (
          snackbar?.text
        )}
        action={snackbar?.cta ? (
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={() => window.open('/pricing', '_blank', 'noopener,noreferrer')}
            sx={{
              bgcolor: 'primary.main',
              color: '#0a0a0a',
              fontWeight: 700,
              fontSize: '0.72rem',
              textTransform: 'none',
              px: 1.4,
              py: 0.35,
              borderRadius: '999px',
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: 'primary.main', filter: 'brightness(1.08)' },
            }}
          >
            プランを見る
          </Button>
        ) : undefined}
        ContentProps={{
          sx: {
            bgcolor: '#26272d',
            color: '#f0f0f0',
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 8px 28px rgba(0,0,0,0.55)',
            fontSize: '0.8rem',
          },
        }}
      />
    </>
  );
};

export default GameLibraryTab;
